import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {ObjUtil} from 'yougile-util'
import {
  createDataObjectId,
  CustomDataField,
  DataObjectId,
  Hub
} from 'yougile-model'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {
  showConfirm,
  showDeleteConfirm
} from '@components/common/base/notifications'
import {createUseStore} from '@containers/hooks'

interface CustomFieldsState {
  draftFields: CustomDataField[]
  originalFields: CustomDataField[]
  wasSubmitted: boolean
  errorBg: boolean
  leaveWithoutSave: boolean
}

export class CustomFieldsStore {
  state: CustomFieldsState = {
    draftFields: [],
    originalFields: [],
    wasSubmitted: false,
    errorBg: false,
    leaveWithoutSave: false
  }

  constructor(public project: Hub) {
    const originalFields = this.getFields()
    this.state.originalFields = originalFields.map(f => ({...f}))
    this.state.draftFields = originalFields.map(f => ({...f}))

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        project: false
      },
      {autoBind: true}
    )

    boardSettingsStore.setOnHasChangesCb(() => {
      this.state.leaveWithoutSave = true
      this.blinkErrorBg()
    })
  }

  get draftFields(): CustomDataField[] {
    return this.state.draftFields
  }

  get wasSubmitted(): boolean {
    return this.state.wasSubmitted
  }

  get errorBg(): boolean {
    return this.state.errorBg
  }

  get leaveWithoutSave(): boolean {
    return this.state.leaveWithoutSave
  }

  get invalidIds(): DataObjectId[] {
    return this.state.draftFields.filter(f => !f.label.trim()).map(f => f.id)
  }

  getFields(): CustomDataField[] {
    return this.project?.data.customFields ?? []
  }

  setFields(fields: CustomDataField[]): void {
    this.state.originalFields = fields
    this.project.data.customFields = fields
    this.project.commit()
  }

  createEmptyField(): CustomDataField {
    return {
      id: createDataObjectId(),
      label: '',
      type: 'string'
    }
  }

  updateChanges(fields: CustomDataField[]): void {
    this.state.draftFields = fields

    const changedFields = fields.filter(field => field.label.trim().length)
    boardSettingsStore.setHasChanges(
      !ObjUtil.deepEqual(changedFields, this.state.originalFields, true)
    )
  }

  async handleChange(
    id: DataObjectId,
    key: keyof CustomDataField,
    value: string
  ): Promise<void> {
    const prev = this.state.draftFields.find(f => f.id === id)
    if (!prev) {
      return
    }

    if (key === 'type' && prev.type !== value) {
      const original = this.state.originalFields.find(f => f.id === id)

      if (original && original.type !== value) {
        const confirmed = await showConfirm(
          {
            title: Lang.msg.crm.boardSettings.fieldTypeChangeTitle,
            subtitle: Lang.msg.crm.boardSettings.fieldTypeChangeSubtitle
          },
          false,
          true
        )
        if (!confirmed) {
          return
        }
      }
    }

    const nextFields = this.state.draftFields.map(f =>
      f.id === id ? {...f, [key]: value} : f
    )
    this.updateChanges(nextFields)
  }

  handleAdd(): void {
    this.updateChanges([...this.state.draftFields, this.createEmptyField()])
  }

  async handleRemove(id: DataObjectId): Promise<void> {
    const originalExists = this.state.originalFields.some(f => f.id === id)
    const nextDraftFields = this.state.draftFields.filter(f => f.id !== id)
    const removeField = this.state.draftFields.find(f => f.id === id)

    if (!originalExists) {
      this.state.draftFields = nextDraftFields
      return
    }

    const confirmed = await showDeleteConfirm(
      {
        title: Lang.msg.crm.boardSettings.fieldDeleteTitle(
          removeField?.label || ''
        ),
        subtitle: Lang.msg.crm.boardSettings.fieldDeleteSubtitle
      },
      false
    )

    if (confirmed) {
      this.updateChanges(nextDraftFields)
    }
  }

  handleSave(): void {
    this.state.wasSubmitted = true
    if (this.invalidIds.length > 0) {
      return
    }

    const updated = [...this.state.draftFields]
    this.setFields(updated)
    this.updateChanges(updated)

    this.state.wasSubmitted = false
    this.state.leaveWithoutSave = false
  }

  handleCancel(): void {
    this.state.draftFields = this.state.originalFields.map(f => ({...f}))
    this.state.wasSubmitted = false
    this.state.leaveWithoutSave = false
    boardSettingsStore.setHasChanges(false)
  }

  blinkErrorBg(): void {
    this.state.errorBg = true
    setTimeout(() => runInAction(() => (this.state.errorBg = false)), 300)
  }
}

const [CustomFieldsContext, useCustomFieldsStore] =
  createUseStore(CustomFieldsStore)
export {CustomFieldsContext, useCustomFieldsStore}
